import { useNavigate } from "react-router-dom";
import { imgSrc, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Lock, Wand2 } from "lucide-react";

export const LicensePicker = ({ licenses, fee, opening, onOpenSession }) => {
  const navigate = useNavigate();
  return (
    <div className="page-container py-10 sm:py-14">
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Creation Studio</p>
      <h1 className="font-display text-3xl tracking-[-0.01em] sm:text-4xl">Pick a licensed digital twin</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Generate e-commerce imagery by draping your garments onto the digital twin of a model you've licensed.
        You never access their raw photos — only the flat, watermarked results. Each generation costs {fmtMoney(fee)}.
      </p>
      {licenses.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed p-12 text-center">
          <Wand2 className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 font-display text-xl">No studio-ready licences yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Book a model and complete payment — once the licence is active, their digital twin unlocks here.
          </p>
          <Button data-testid="studio-explore-button" className="mt-4 rounded-xl" onClick={() => navigate("/explore")}>Explore models</Button>
        </div>
      ) : (
        <div data-testid="studio-license-grid" className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {licenses.map((lic) => {
            const img = imgSrc(lic.model_image);
            const daysLeft = Math.max(Math.ceil((new Date(lic.end_at).getTime() - Date.now()) / 86400000), 0);
            return (
              <Card key={lic.id} data-testid="studio-license-card" className="overflow-hidden rounded-2xl">
                <CardContent className="flex gap-4 p-4">
                  <div className="h-28 w-20 shrink-0 overflow-hidden rounded-xl bg-muted">
                    {img && <img src={img} alt={lic.model_name} className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-lg leading-tight">{lic.model_name}</h3>
                    <p className="font-mono-plex text-[10px] text-muted-foreground">Licence {lic.id.slice(0, 8).toUpperCase()} · {daysLeft}d left</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <Badge variant="secondary" className="gap-1 rounded-full text-[10px]">
                        <Lock className="h-2.5 w-2.5" /> Encrypted twin v{lic.twin.version}
                      </Badge>
                      <Badge variant="secondary" className="rounded-full text-[10px]">{lic.generation_count} generated</Badge>
                    </div>
                    <Button
                      data-testid="studio-open-session-button"
                      size="sm"
                      disabled={opening === lic.id}
                      onClick={() => onOpenSession(lic)}
                      className="mt-3 rounded-xl"
                    >
                      {opening === lic.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1.5 h-3.5 w-3.5" />}
                      {lic.open_session_id ? "Resume session" : "Open studio session"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <p className="mt-8 text-[11px] leading-relaxed text-muted-foreground">
        Every twin is a private, AES-256 encrypted AI profile locked to LOOKBOOK. Exports are flat, digitally
        watermarked images carrying the licence registry ID — the underlying profile is never downloadable.
      </p>
    </div>
  );
};
